import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from '../Context/TranslationContext';
import { Target, Award, Users, Medal, BookOpen, CheckSquare } from 'lucide-react';

const GoldenArrowPage = () => {
  const { isTamil } = useTranslation();

  const translations = {
    title: {
      en: "Golden Arrow Badge",
      ta: "கோல்டன் அரோ பேட்ஜ்"
    },
    subtitle: {
      en: "Advanced Section Badge in Scout/Guide Advancement",
      ta: "சாரணர்/சாரணியர் முன்னேற்றத்தில் உயர்நிலை பிரிவு பேட்ஜ்"
    },
    description: {
      en: "The Golden Arrow Badge is a significant achievement in the Advanced Section of Scouting and Guiding, demonstrating proficiency in advanced scouting skills and leadership abilities. This badge represents a scout's/guide's progression through various stages of skill development and service.",
      ta: "கோல்டன் அரோ பேட்ஜ் என்பது சாரணர் மற்றும் சாரணியர் உயர்நிலை பிரிவில் ஒரு முக்கியமான சாதனையாகும், மேம்பட்ட சாரணர் திறன்கள் மற்றும் தலைமைத்துவ திறன்களில் தேர்ச்சியைக் காட்டுகிறது. இந்த பேட்ஜ் திறன் மேம்பாடு மற்றும் சேவையின் பல்வேறு நிலைகளில் ஒரு சாரணர்/சாரணியரின் முன்னேற்றத்தைக் குறிக்கிறது."
    },
    sections: {
      about: {
        title: {
          en: "About the Badge",
          ta: "பேட்ஜ் பற்றி"
        },
        content: {
          en: [
            "The Golden Arrow Badge is part of the Advanced Section proficiency badges in Bharat Scouts and Guides.",
            "It signifies expertise in advanced scouting techniques and leadership capabilities.",
            "The badge represents the scout's/guide's journey in mastering essential outdoor and leadership skills.",
            "Recipients demonstrate proficiency in various scouting activities and community service."
          ],
          ta: [
            "கோல்டன் அரோ பேட்ஜ் பாரத சாரணர் மற்றும் சாரணியர் உயர்நிலை பிரிவு தேர்ச்சி பேட்ஜ்களில் ஒரு பகுதியாகும்.",
            "இது மேம்பட்ட சாரணர் நுட்பங்கள் மற்றும் தலைமைத்துவ திறன்களில் நிபுணத்துவத்தைக் குறிக்கிறது.",
            "இந்த பேட்ஜ் அத்தியாவசிய வெளிப்புற மற்றும் தலைமைத்துவ திறன்களை கைவரப்பெறுவதில் சாரணர்/சாரணியரின் பயணத்தை குறிக்கிறது.",
            "பெறுநர்கள் பல்வேறு சாரணர் செயல்பாடுகள் மற்றும் சமூக சேவையில் தேர்ச்சியை வெளிப்படுத்துகிறார்கள்."
          ]
        }
      },
      requirements: {
        title: {
          en: "Badge Requirements",
          ta: "பேட்ஜ் தேவைகள்"
        },
        prerequisites: {
          title: {
            en: "Prerequisites",
            ta: "முன்தேவைகள்"
          },
          content: {
            en: [
              "Completion of Rajya Puraskar requirements",
              "Active participation in unit activities",
              "Regular attendance in scout/guide meetings",
              "Completion of required service hours",
              "Demonstration of scouting skills"
            ],
            ta: [
              "ராஜ்ய புரஸ்கார் தேவைகளை நிறைவு செய்தல்",
              "அலகு செயல்பாடுகளில் செயலில் பங்கேற்பு",
              "சாரணர்/சாரணியர் கூட்டங்களில் வழக்கமான வருகை",
              "தேவையான சேவை நேரங்களை நிறைவு செய்தல்",
              "சாரணர் திறன்களின் செயல்விளக்கம்"
            ]
          }
        },
        skills: {
          title: {
            en: "Required Skills",
            ta: "தேவையான திறன்கள்"
          },
          content: {
            en: [
              {
                category: "Outdoor Skills",
                items: [
                  "Advanced campcraft and pioneering",
                  "Navigation and map reading",
                  "Nature study and conservation",
                  "Survival techniques"
                ]
              },
              {
                category: "Leadership Skills",
                items: [
                  "Team management",
                  "Event planning",
                  "Communication",
                  "Problem-solving"
                ]
              },
              {
                category: "Service Skills",
                items: [
                  "First aid and emergency response",
                  "Community service project management",
                  "Environmental conservation",
                  "Public health awareness"
                ]
              }
            ],
            ta: [
              {
                category: "வெளிப்புற திறன்கள்",
                items: [
                  "மேம்பட்ட முகாமிடல் மற்றும் முன்னோடி",
                  "வழிசெலுத்தல் மற்றும் வரைபடம் வாசித்தல்",
                  "இயற்கை ஆய்வு மற்றும் பாதுகாப்பு",
                  "உயிர்வாழ்வு நுட்பங்கள்"
                ]
              },
              {
                category: "தலைமைத்துவ திறன்கள்",
                items: [
                  "குழு மேலாண்மை",
                  "நிகழ்வு திட்டமிடல்",
                  "தகவல் தொடர்பு",
                  "சிக்கல் தீர்த்தல்"
                ]
              },
              {
                category: "சேவை திறன்கள்",
                items: [
                  "முதலுதவி மற்றும் அவசர பதில்",
                  "சமூக சேவை திட்ட மேலாண்மை",
                  "சுற்றுச்சூழல் பாதுகாப்பு",
                  "பொது சுகாதார விழிப்புணர்வு"
                ]
              }
            ]
          }
        },
        testing: {
          title: {
            en: "Testing Process",
            ta: "சோதனை செயல்முறை"
          },
          stages: {
            en: [
              {
                stage: "Practical Assessment",
                details: "Demonstration of required skills in real scenarios"
              },
              {
                stage: "Written Evaluation",
                details: "Test of theoretical knowledge and problem-solving abilities"
              },
              {
                stage: "Project Work",
                details: "Planning and execution of a community service project"
              },
              {
                stage: "Documentation",
                details: "Presentation of progress records and achievements"
              }
            ],
            ta: [
              {
                stage: "செயல்முறை மதிப்பீடு",
                details: "உண்மையான சூழ்நிலைகளில் தேவையான திறன்களின் செயல்விளக்கம்"
              },
              {
                stage: "எழுத்து மதிப்பீடு",
                details: "கோட்பாட்டு அறிவு மற்றும் சிக்கல் தீர்க்கும் திறன்களின் சோதனை"
              },
              {
                stage: "திட்டப்பணி",
                details: "சமூக சேவை திட்டத்தின் திட்டமிடல் மற்றும் செயல்படுத்துதல்"
              },
              {
                stage: "ஆவணப்படுத்தல்",
                details: "முன்னேற்ற பதிவுகள் மற்றும் சாதனைகளின் விளக்கக்காட்சி"
              }
            ]
          }
        }
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-16 mt-10">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-6xl mx-auto"
        >
          {/* Header Section */}
          <div className="text-center mb-16">
            <div className="inline-block p-2 px-4 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold mb-4">
              {isTamil ? 'உயர்நிலை பிரிவு பேட்ஜ்' : 'Advanced Section Badge'}
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              {translations.title[isTamil ? 'ta' : 'en']}
            </h1>
            <h2 className="text-xl md:text-2xl text-blue-600 font-semibold mb-6">
              {translations.subtitle[isTamil ? 'ta' : 'en']}
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              {translations.description[isTamil ? 'ta' : 'en']}
            </p>
          </div>

          {/* Badge Image */}
          <div className="mb-16 rounded-lg overflow-hidden shadow-2xl">
            <img 
              src="/Images/gab.png"
              alt="Golden Arrow Badge"
              className="w-20% max-w-md mx-auto"
            />
          </div>

          {/* About Section */}
          <div className="bg-white rounded-xl shadow-md p-8 mb-8">
            <div className="flex items-center mb-6">
              <Award className="w-8 h-8 text-blue-600 mr-4" />
              <h2 className="text-2xl font-bold text-gray-900">
                {translations.sections.about.title[isTamil ? 'ta' : 'en']}
              </h2>
            </div>
            <div className="space-y-4">
              {translations.sections.about.content[isTamil ? 'ta' : 'en'].map((item, index) => (
                <p key={index} className="text-gray-700 leading-relaxed">
                  {item}
                </p>
              ))}
            </div>
          </div>

          {/* Prerequisites */}
          <div className="bg-white rounded-xl shadow-md p-8 mb-8">
            <div className="flex items-center mb-6">
              <Target className="w-8 h-8 text-blue-600 mr-4" />
              <h2 className="text-2xl font-bold text-gray-900">
                {translations.sections.requirements.prerequisites.title[isTamil ? 'ta' : 'en']}
              </h2>
            </div>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {translations.sections.requirements.prerequisites.content[isTamil ? 'ta' : 'en'].map((item, index) => (
                <li key={index} className="flex items-start">
                  <CheckSquare className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Required Skills */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            {translations.sections.requirements.skills.content[isTamil ? 'ta' : 'en'].map((skillCategory, index) => (
              <div key={index} className="bg-white rounded-xl shadow-md p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Medal className="w-5 h-5 text-blue-600 mr-2" />
                  {skillCategory.category}
                </h3>
                <ul className="space-y-3">
                  {skillCategory.items.map((skill, skillIndex) => (
                    <li key={skillIndex} className="flex items-start text-gray-700">
                      <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                      {skill}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Testing Process */}
          <div className="bg-white rounded-xl shadow-md p-8 mb-8">
            <div className="flex items-center mb-8">
              <BookOpen className="w-8 h-8 text-blue-600 mr-4" />
              <h2 className="text-2xl font-bold text-gray-900">
                {translations.sections.requirements.testing.title[isTamil ? 'ta' : 'en']}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {translations.sections.requirements.testing.stages[isTamil ? 'ta' : 'en'].map((stage, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold mr-3">
                      {index + 1}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">{stage.stage}</h3>
                  </div>
                  <p className="text-gray-700">{stage.details}</p>
                </div>
              ))}
            </div>
          </div>

      
        </motion.div>
      </div>
    </div>
  );
};

export default GoldenArrowPage;